"use client";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

interface LoadingState {
  isLoading: boolean;
  message?: string;
}

interface LoadingContextValue {
  isLoading: boolean;
  message?: string;
  showLoading: (message?: string) => void;
  hideLoading: () => void;
  withLoading: <T>(task: Promise<T> | (() => Promise<T>), message?: string) => Promise<T>;
}

const LoadingContext = createContext<LoadingContextValue | null>(null);

export function LoadingProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<LoadingState>({ isLoading: false });
  const counterRef = useRef(0);

  const showLoading = useCallback((message?: string) => {
    counterRef.current += 1;
    setState({ isLoading: true, message });
  }, []);

  const hideLoading = useCallback(() => {
    counterRef.current = Math.max(0, counterRef.current - 1);
    if (counterRef.current === 0) {
      setState({ isLoading: false });
    }
  }, []);

  const withLoading = useCallback(
    async <T,>(task: Promise<T> | (() => Promise<T>), message?: string): Promise<T> => {
      showLoading(message);
      try {
        return await (typeof task === "function" ? task() : task);
      } finally {
        hideLoading();
      }
    },
    [showLoading, hideLoading]
  );

  const value = useMemo<LoadingContextValue>(
    () => ({
      isLoading: state.isLoading,
      message: state.message,
      showLoading,
      hideLoading,
      withLoading,
    }),
    [state.isLoading, state.message, showLoading, hideLoading, withLoading]
  );

  return <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>;
}

export function useLoading(): LoadingContextValue {
  const ctx = useContext(LoadingContext);
  if (!ctx) {
    throw new Error("useLoading must be used within LoadingProvider");
  }
  return ctx;
}
